import {
  type ForManagerLocationDetail,
  getLocationsRetrieveQueryKey,
  getStafferOrganizationsRetrieveQueryKey,
  type OrganizationRetrieveResponse,
  usePaymentsInvoicingCustomerUpdate,
} from "@sidegigllc/api/client";
import { useQueryClient } from "@tanstack/react-query";
import type { PaymentSourceId } from "./useSaveStripePaymentMethod";

/** PUTs the source's Invoiced.com customer id and writes the saved id back into the source's retrieve. */
export function useSaveInvoicedCustomerId(sourceId: PaymentSourceId) {
  const queryClient = useQueryClient();
  const isOrganization = "organizationId" in sourceId;
  const queryKey = isOrganization
    ? getStafferOrganizationsRetrieveQueryKey(sourceId.organizationId)
    : getLocationsRetrieveQueryKey(sourceId.locationId);

  const mutation = usePaymentsInvoicingCustomerUpdate({
    mutation: {
      onSuccess: ({ customer_id }) => {
        // The seed shows the saved id at once, so Save needn't wait on (or fail with) the refetch below.
        queryClient.setQueryData<ForManagerLocationDetail | OrganizationRetrieveResponse>(
          queryKey,
          (entity) => entity && { ...entity, invoiced_customer_id: customer_id },
        );
        void queryClient.invalidateQueries({ queryKey });
      },
    },
  });

  return {
    ...mutation,
    save: (customerId: string, callbacks: Parameters<typeof mutation.mutate>[1]) =>
      mutation.mutate(
        {
          data: { customer_id: customerId },
          params: isOrganization ? { organization_id: sourceId.organizationId } : { location_id: sourceId.locationId },
        },
        callbacks,
      ),
  };
}
